import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Badge, Modal, ErrorNote, fmtPKR, MONTHS } from '../components/ui.jsx';

export default function PayrollRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [payslip, setPayslip] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api(`/payroll/runs/${id}`).then(setRun).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [id]);

  if (error) return <div className="empty-state"><p>{error}</p></div>;
  if (!run) return <div className="empty-state"><p>Loading payroll…</p></div>;

  const period = `${MONTHS[run.month - 1]} ${run.year}`;
  const totals = run.items.reduce((t, i) => ({
    gross: t.gross + Number(i.grossPay),
    deducted: t.deducted + Number(i.advanceDeducted),
    net: t.net + Number(i.netPay),
  }), { gross: 0, deducted: 0, net: 0 });

  const approve = async () => {
    setBusy(true); setError('');
    try {
      await api(`/payroll/runs/${id}/approve`, { method: 'POST', body: { signature } });
      setApproveOpen(false);
      load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try { await api(`/payroll/runs/${id}/mark-paid`, { method: 'POST', body: {} }); load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="page-header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/payroll')}>← All runs</button>
        <h2>{period}</h2>
        <Badge kind={run.status === 'DRAFT' ? 'half-day' : 'active'}>{run.status}</Badge>
        <div style={{ flex: 1 }} />
        {run.status === 'DRAFT' && (
          <button className="btn btn-success" onClick={() => setApproveOpen(true)}>Approve & Lock</button>
        )}
        {run.status === 'APPROVED' && (
          <button className="btn btn-primary" onClick={markPaid} disabled={busy}>Mark as Paid</button>
        )}
      </div>

      {run.approvedBy && (
        <div className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
          Approved by {run.approvedBy.name} on {new Date(run.approvedAt).toLocaleString('en-GB')} — signature: “{run.signature}”
        </div>
      )}

      <div className="payroll-summary" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        <div className="pay-stat"><div className="pay-stat-label">Total Gross</div><div className="pay-stat-value">{fmtPKR(totals.gross)}</div></div>
        <div className="pay-stat"><div className="pay-stat-label">Advance Recovery</div><div className="pay-stat-value text-amber">−{fmtPKR(totals.deducted)}</div></div>
        <div className="pay-stat"><div className="pay-stat-label">Net Payable</div><div className="pay-stat-value text-green">{fmtPKR(totals.net)}</div></div>
      </div>

      <ErrorNote error={error} />

      <div className="card emp-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Employee</th><th>Site</th><th>Days</th><th>OT hrs</th>
              <th>Gross</th><th>Advance</th><th>Net Pay</th><th></th>
            </tr>
          </thead>
          <tbody>
            {run.items.map((i) => (
              <tr key={i.id}>
                <td>
                  <div className="emp-name">{i.employee.name}</div>
                  <div className="emp-desg">{i.employee.code} · {i.employee.designation}</div>
                </td>
                <td><span className="site-pill">{i.employee.site.name}</span></td>
                <td>{i.daysPresent}{i.halfDays > 0 && <span className="text-amber"> +{i.halfDays}½</span>}</td>
                <td>{Number(i.overtimeHours) || '—'}</td>
                <td>{fmtPKR(i.grossPay)}</td>
                <td className={Number(i.advanceDeducted) ? 'text-amber' : 'text-muted'}>
                  {Number(i.advanceDeducted) ? '−' + fmtPKR(i.advanceDeducted) : '—'}
                </td>
                <td className="text-green fw-bold">{fmtPKR(i.netPay)}</td>
                <td><button className="btn btn-ghost btn-sm" onClick={() => setPayslip(i)}>Payslip</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!payslip} onClose={() => setPayslip(null)} title="Payslip">
        {payslip && (
          <div className="print-area">
            <div className="payslip-header">
              <h2>RMG HR — Payslip</h2>
              <p>{period} · Generated {new Date().toLocaleDateString('en-GB')}</p>
            </div>
            <div className="payslip-row"><span className="lbl">Employee</span><span className="val">{payslip.employee.name} ({payslip.employee.code})</span></div>
            <div className="payslip-row"><span className="lbl">Designation / Site</span><span className="val">{payslip.employee.designation} · {payslip.employee.site.name}</span></div>
            <div className="payslip-row"><span className="lbl">Daily Rate</span><span className="val">{fmtPKR(payslip.dailyRate)}</span></div>
            <div className="payslip-row"><span className="lbl">Days Present / Half / Absent</span><span className="val">{payslip.daysPresent} / {payslip.halfDays} / {payslip.daysAbsent}</span></div>
            <div className="payslip-row"><span className="lbl">Base Pay</span><span className="val">{fmtPKR(payslip.basePay)}</span></div>
            <div className="payslip-row"><span className="lbl">Overtime ({Number(payslip.overtimeHours)}h @ 1.5×)</span><span className="val">{fmtPKR(payslip.overtimePay)}</span></div>
            <div className="payslip-row"><span className="lbl">Gross Pay</span><span className="val">{fmtPKR(payslip.grossPay)}</span></div>
            {payslip.deductions.map((d) => (
              <div className="payslip-row" key={d.id}>
                <span className="lbl">Advance recovery — {d.advance.reason || 'advance'}</span>
                <span className="val text-amber">−{fmtPKR(d.amount)}</span>
              </div>
            ))}
            <div className="payslip-total"><span>Net Pay</span><span className="val">{fmtPKR(payslip.netPay)}</span></div>
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }} className="no-print">
              <button className="btn btn-primary" onClick={() => window.print()}>Print / Save PDF</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={approveOpen} onClose={() => setApproveOpen(false)} title={`Approve ${period} payroll`}>
        <p style={{ fontSize: 13, color: 'var(--txt2)', marginBottom: 16 }}>
          Approving locks this payroll and the month's attendance permanently, and finalizes
          {' '}{fmtPKR(totals.net)} in net payments. Type your full name as a digital signature.
        </p>
        <div className="form-group">
          <label>Digital Signature</label>
          <input className="form-input" value={signature} onChange={(e) => setSignature(e.target.value)}
            placeholder="Your full name" />
        </div>
        <ErrorNote error={error} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={() => setApproveOpen(false)}>Cancel</button>
          <button className="btn btn-success" onClick={approve} disabled={busy || signature.trim().length < 3}>
            {busy ? 'Approving…' : 'Sign & Approve'}
          </button>
        </div>
      </Modal>
    </>
  );
}
